import React, { useState } from 'react'
import classNames from 'classnames'
import ReactSelect, { GroupBase, Props as SelectProps } from 'react-select'
import styles from './Select.module.scss'
import Tooltip from '../Tooltip/Tooltip'
import { useHover } from 'react-use'
import Info from '../Svgs/Info'

export interface Props<Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>
  extends SelectProps<Option, IsMulti, Group> {
  className?: string
  label?: string
  labelHint?: string
  required?: boolean
  description?: string
  name: string
  id?: string
  error?: string | boolean
  noOutline?: boolean
  style?: object
}

type State = {
  isOpen: boolean
}

function Select<Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>({
  className,
  id,
  label,
  labelHint,
  required,
  description,
  name,
  isSearchable,
  options = [],
  isDisabled,
  placeholder = 'Select...',
  error,
  style = {},
  noOutline,
  ...props
}: Props<Option, IsMulti, Group>) {
  const _id = id || name

  const [state, setState] = useState<State>({
    isOpen: false,
  })

  const classes = classNames([styles.root, className], {
    [styles['no-outline']]: noOutline,
    [styles['has-error']]: !!error,
    [styles['is-open']]: state.isOpen,
  })

  const [hoverableInfoIcon, isHovering] = useHover(
    <div className={styles['info-icon']}>
      <Info width={20} height={20} />
    </div>,
  )

  return (
    <div className={classes} style={style}>
      {label && (
        <label htmlFor={_id}>
          {label}
          {required && <span className={styles.required}> *</span>}
          {error && ` - ${error as string}`}
          {labelHint && (
            <Tooltip isOpen={isHovering} body={labelHint}>
              {hoverableInfoIcon}
            </Tooltip>
          )}
        </label>
      )}

      {description && <p className={styles.description}>{description}</p>}

      <ReactSelect
        {...props}
        id={_id}
        onMenuClose={() => setState({ ...state, isOpen: false })}
        onMenuOpen={() => setState({ ...state, isOpen: true })}
        name={name}
        placeholder={placeholder}
        options={options}
        isDisabled={isDisabled}
        isClearable={false}
        isSearchable={isSearchable}
        classNamePrefix="react-select"
      />
    </div>
  )
}

export default Select
